import { assertStaffCapability, assertStaffRelationship, isSupervisor, transitionActorId, } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { planConversationTransition, } from "./conversation.js";
import { AuthorizationDeniedError, DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
import { skippedEvidence } from "./routing.js";
import { sha256HexOfText } from "./sha256.js";
// Frozen: the database CHECK constraints are generated from these tuples.
export const handoffStatuses = Object.freeze([
    "REQUESTED",
    "WAITING",
    "ACCEPTED",
    "RELEASED",
    "CANCELLED",
]);
export const handoffTerminalStatuses = Object.freeze(["RELEASED", "CANCELLED"]);
export const handoffRequesters = Object.freeze([
    "CUSTOMER",
    "AI_POLICY",
    "STAFF",
    "SYSTEM",
]);
/** The nine triggers of the spec's handoff list. */
export const handoffReasonCodes = Object.freeze([
    "CUSTOMER_REQUESTED_PERSON",
    "SUPPLIER_CONTACT_REQUIRED",
    "CONCIERGE_JUDGMENT_REQUIRED",
    "CATALOG_DATA_CONFLICT",
    "UNSUPPORTED_HIGH_IMPACT_REQUEST",
    "COMPLAINT_SERVICE_RECOVERY",
    "PAYMENT_FULFILMENT_EXCEPTION",
    "REPEATED_NO_PROGRESS",
    "POLICY_REVIEW_REQUIRED",
]);
/** DIRECT is a supervisor's reassignment to a named staff member. */
export const handoffTargetKinds = Object.freeze([
    "TEMPORARY",
    "PRIMARY",
    "BACKUP",
    "SUPERVISOR_QUEUE",
    "UNASSIGNED",
    "DIRECT",
]);
export const handoffEventTypes = Object.freeze([
    "REQUESTED",
    "ROUTED",
    "REASSIGNED",
    "ACCEPTED",
    "OWNER_TRANSFERRED",
    "RELEASED",
    "CANCELLED",
]);
export const handoffReasonsByRequester = Object.freeze({
    CUSTOMER: Object.freeze(["CUSTOMER_REQUESTED_PERSON", "COMPLAINT_SERVICE_RECOVERY"]),
    AI_POLICY: handoffReasonCodes,
    STAFF: handoffReasonCodes,
    SYSTEM: Object.freeze([
        "REPEATED_NO_PROGRESS",
        "PAYMENT_FULFILMENT_EXCEPTION",
        "POLICY_REVIEW_REQUIRED",
        "CATALOG_DATA_CONFLICT",
    ]),
});
/**
 * Why a supervisor moves a handoff to a staff member who is on a manual unavailability window (CHT-731).
 * A closed code, audited. Without it, an unavailable target is refused (`handoff.target_unavailable`).
 * The list repeats `handoffAvailabilityOverrideValues` of `@lafayette/schemas`.
 */
export const handoffAvailabilityOverrideCodes = Object.freeze([
    "COVERAGE_GAP",
    "CUSTOMER_URGENT",
    "SUPERVISOR_JUDGMENT",
]);
/**
 * Why a WAITING handoff moves on to the next coverage (CHT-734). A closed code, never free text, so it can go to
 * the audit row, the history row and the metrics. The list repeats `handoffEscalationReasonValues` of
 * `@lafayette/schemas` (parity-tested). `PRIMARY_UNAVAILABLE` and `NOT_ACCEPTED_IN_TIME` are conditions the
 * command checks under its locks; `SUPERVISOR_OVERRIDE` is a supervisor's call and needs none.
 */
export const handoffEscalationReasonCodes = Object.freeze([
    "PRIMARY_UNAVAILABLE",
    "NOT_ACCEPTED_IN_TIME",
    "SUPERVISOR_OVERRIDE",
]);
/** The most escalations one handoff gets. The database allows 20; the ranking has four steps, so this is a backstop. */
export const MAX_HANDOFF_ESCALATIONS = 10;
/** The transition table (H0 is the insert). The database guard (LF008) allows exactly the 7 status pairs. */
export const handoffTransitions = Object.freeze([
    { id: "H0", event: "request", from: null, to: "REQUESTED", conversationEvent: "handoff.request" },
    {
        id: "H1",
        event: "route",
        from: "REQUESTED",
        to: "WAITING",
        conversationEvent: "handoff.route",
    },
    {
        id: "H2",
        event: "reassign",
        from: "WAITING",
        to: "WAITING",
        conversationEvent: "handoff.reassign",
    },
    {
        id: "H3",
        event: "accept",
        from: "WAITING",
        to: "ACCEPTED",
        conversationEvent: "handoff.accept",
    },
    {
        id: "H4",
        event: "transfer_owner",
        from: "ACCEPTED",
        to: "ACCEPTED",
        conversationEvent: "handoff.transfer_owner",
    },
    {
        id: "H5",
        event: "release",
        from: "ACCEPTED",
        to: "RELEASED",
        conversationEvent: "handoff.release",
    },
    {
        id: "H6",
        event: "cancel",
        from: "REQUESTED",
        to: "CANCELLED",
        conversationEvent: "handoff.cancel",
    },
    {
        id: "H7",
        event: "cancel",
        from: "WAITING",
        to: "CANCELLED",
        conversationEvent: "handoff.cancel",
    },
]);
export function isAllowedHandoffStatusPair(from, to) {
    return handoffTransitions.some((row) => row.from === from && row.to === to);
}
/** Refuses a status pair outside the table, whatever the actor: `handoff.transition_invalid`. */
export function assertHandoffStatusTransition(from, to) {
    if (!isAllowedHandoffStatusPair(from, to)) {
        throw new DomainRuleViolationError("handoff.transition_invalid");
    }
}
// The conversation mode that coincides with each open handoff status (LF009 checks the same pairs).
const MODE_OF_OPEN_STATUS = {
    REQUESTED: "HANDOFF_REQUESTED",
    WAITING: "WAITING_FOR_CONCIERGE",
    ACCEPTED: "HUMAN_ACTIVE",
};
const denied = (code) => new AuthorizationDeniedError(code);
const violation = (code) => new DomainRuleViolationError(code);
/**
 * The first check of a staff command on a handoff, before any check of its status (R24): the role
 * (`assertStaffCapability`), then ANY staff relationship to the handoff (its current target or
 * owner, an effective assignment to the customer, or the supervisor role). A staff user with the
 * role and no relationship gets `relationship_missing` in every status, so the status class is not
 * revealed. A related actor passes and keeps the status errors. The rule of the command itself
 * still runs afterwards, because it may allow fewer relationships than this gate.
 */
function assertStaffReachesHandoff(actor, capability, environment) {
    const staff = assertStaffCapability(actor, capability, environment);
    assertStaffRelationship(staff.authority, staff.relationship, "RESOURCE");
    return staff;
}
/** The reason a staff-internal summary may carry: 1..1000 characters, a newline allowed. */
export function handoffReasonSummary(value) {
    return boundedText("reasonSummary", value, 1000, { multiline: true });
}
function requireReason(reason) {
    if (reason === null)
        throw new DomainValidationError("reason", "is required");
    return reason;
}
function checkVersion(expected, handoff) {
    if (expected === null)
        throw new DomainValidationError("expectedVersion", "is required");
    if (expected !== handoff.version)
        throw violation("handoff.version_conflict");
}
function modeOf(handoff) {
    if (handoff.status === "REQUESTED" ||
        handoff.status === "WAITING" ||
        handoff.status === "ACCEPTED") {
        return MODE_OF_OPEN_STATUS[handoff.status];
    }
    throw violation("handoff.transition_invalid");
}
function eventPlan(actor, eventType, statusAfter, rest) {
    return Object.freeze({
        eventType,
        statusAfter,
        actorKind: actor.kind,
        actorId: transitionActorId(actor),
        targetKind: rest.targetKind ?? null,
        targetStaffUserId: rest.targetStaffUserId ?? null,
        ownerStaffUserId: rest.ownerStaffUserId ?? null,
        internalReason: rest.internalReason ?? null,
    });
}
function transitionPlan(event, handoff, to, parts) {
    const rule = handoffTransitions.find((row) => row.event === event && row.from === handoff.status && row.to === to);
    if (rule === undefined)
        throw violation("handoff.transition_invalid");
    return Object.freeze({
        event,
        from: handoff.status,
        to,
        basis: parts.basis ?? null,
        patch: parts.patch,
        conversation: planConversationTransition(modeOf(handoff), rule.conversationEvent),
        historyEvent: parts.historyEvent,
        auditAction: parts.auditAction,
        availabilityOverride: parts.availabilityOverride ?? null,
        outcome: parts.outcome ?? null,
    });
}
// --- H0 request --------------------------------------------------------------------------------
export function planHandoffRequest(input) {
    const { conversation, actor } = input;
    // 0. A staff actor is checked first, whatever the conversation's state (R24).
    if (actor.kind === "staff") {
        assertStaffReachesHandoff(actor, "handoff.request_for_customer", input.environment);
    }
    // 1. The conversation: only AI_ACTIVE with no open handoff may start one.
    if (conversation.activeHandoffId !== null)
        throw violation("handoff.already_open");
    const conversationPlan = planConversationTransition(conversation.mode, "handoff.request");
    // 2. The actor decides the requester.
    let requester;
    let requestedByCustomerId = null;
    let requestedByStaffUserId = null;
    let requestedBySystemActor = null;
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== conversation.customerId)
                throw denied("not_owner");
            requester = "CUSTOMER";
            requestedByCustomerId = actor.customerId;
            break;
        case "staff": {
            assertStaffCapability(actor, "handoff.request_for_customer", input.environment);
            assertStaffRelationship(actor.authority, actor.relationship, "CUSTOMER_DATA");
            requester = "STAFF";
            requestedByStaffUserId = actor.authority.staffUserId;
            break;
        }
        case "system":
            if (actor.actorId === "ai-handoff-policy")
                requester = "AI_POLICY";
            else if (actor.actorId === "handoff-policy")
                requester = "SYSTEM";
            else
                throw denied("role_missing");
            requestedBySystemActor = actor.actorId;
            break;
    }
    // 3. An AI policy run must still hold the current generation.
    if (requester === "AI_POLICY" &&
        input.presentedGeneration !== conversation.aiResponseGeneration) {
        throw violation("handoff.generation_stale");
    }
    // 4. Evidence: the reason code the requester may use, the staff reason, the summary.
    if (!handoffReasonsByRequester[requester].includes(input.reasonCode)) {
        throw violation("handoff.reason_not_allowed");
    }
    const staffReason = requester === "STAFF" ? requireReason(input.staffReason) : null;
    const reasonSummary = input.reasonSummary === null ? null : handoffReasonSummary(input.reasonSummary);
    return Object.freeze({
        handoff: Object.freeze({
            status: "REQUESTED",
            requestedBy: requester,
            requestedByCustomerId,
            requestedByStaffUserId,
            requestedBySystemActor,
            reasonCode: input.reasonCode,
            reasonSummary,
            requestedGeneration: conversation.aiResponseGeneration + (conversationPlan.incrementsGeneration ? 1 : 0),
        }),
        conversation: conversationPlan,
        historyEvent: eventPlan(actor, "REQUESTED", "REQUESTED", {
            internalReason: staffReason ?? (actor.kind === "system" ? `policy:${actor.actorId}` : null),
        }),
        auditAction: requester === "STAFF" ? "handoff.requested_by_staff" : "handoff.requested",
    });
}
// --- H1 route ----------------------------------------------------------------------------------
export function planHandoffRoute(input) {
    const { handoff, decision, actor } = input;
    if (handoff.status !== "REQUESTED")
        throw violation("handoff.not_requested");
    if (actor.kind !== "system" || actor.actorId !== "handoff-routing")
        throw denied("role_missing");
    const targetStaffUserId = "staffUserId" in decision ? decision.staffUserId : null;
    const targetAssignmentId = "assignmentId" in decision ? decision.assignmentId : null;
    return transitionPlan("route", handoff, "WAITING", {
        patch: {
            kind: "route",
            targetKind: decision.kind,
            targetStaffUserId,
            targetAssignmentId,
            routedAt: "now",
        },
        historyEvent: eventPlan(actor, "ROUTED", "WAITING", {
            targetKind: decision.kind,
            targetStaffUserId,
            internalReason: `routing:${decision.kind.toLowerCase()}`,
        }),
        auditAction: "handoff.routed",
        outcome: {
            targetKind: decision.kind,
            targetStaffUserId,
            fallback: decision.fallback,
            degraded: decision.degraded,
        },
    });
}
/**
 * Who may accept a WAITING handoff and on what basis: the routed target, a concierge with an
 * effective assignment to the customer (fallback), or a supervisor, in that order of precedence.
 * Checks run the staff gate (role, then any relationship; see `assertStaffReachesHandoff`), then
 * status, then the acceptance basis, then version. The gate comes first (R24), so a staff user with
 * no relationship gets the same refusal in every status. Status comes before the basis and the
 * version, so the loser of a race gets the deterministic `handoff.not_waiting` rather than a
 * version conflict.
 */
export function decideHandoffAcceptance(input) {
    const actor = assertStaffReachesHandoff(input.actor, "handoff.accept", input.environment);
    if (input.handoff.status !== "WAITING")
        throw violation("handoff.not_waiting");
    const basis = acceptanceBasis(actor);
    checkVersion(input.expectedVersion, input.handoff);
    return { basis };
}
function acceptanceBasis(actor) {
    if (actor.relationship.isTarget)
        return "TARGET";
    if (actor.relationship.hasEffectiveAssignment)
        return "FALLBACK_CONCIERGE";
    if (isSupervisor(actor.authority))
        return "SUPERVISOR";
    throw denied("relationship_missing");
}
export function planHandoffAccept(input) {
    const { basis } = decideHandoffAcceptance(input);
    const actor = input.actor;
    const owner = actor.authority.staffUserId;
    return transitionPlan("accept", input.handoff, "ACCEPTED", {
        basis,
        patch: { kind: "accept", acceptedByStaffUserId: owner, acceptedAt: "now" },
        historyEvent: eventPlan(actor, "ACCEPTED", "ACCEPTED", {
            targetKind: input.handoff.targetKind,
            targetStaffUserId: input.handoff.targetStaffUserId,
            ownerStaffUserId: owner,
        }),
        auditAction: "handoff.accepted",
    });
}
/**
 * Who may move a handoff (contract section 7, T5 and T7): a supervisor; the current target while it WAITS
 * (to an eligible colleague); the current owner while it is ACCEPTED. A staff member with only a coverage
 * assignment may accept (basis FALLBACK_CONCIERGE) but may not move it.
 */
function assertMayMove(staff, status) {
    const { isOwner, isTarget } = staff.relationship;
    const allowed = isSupervisor(staff.authority) ||
        (status === "WAITING" && isTarget) ||
        (status === "ACCEPTED" && isOwner);
    if (!allowed)
        throw denied("relationship_missing");
}
/**
 * The checks of the new holder, after the version and the reason: an override only from a supervisor, then
 * eligible, then a change of person, then available (or the supervisor's code). Returns the code that was used.
 */
function checkNewHolder(staff, holder, currentHolder, override) {
    if (override !== null && !isSupervisor(staff.authority))
        throw denied("role_missing");
    if (!holder.eligible)
        throw violation("handoff.target_ineligible");
    if (holder.staffUserId === currentHolder)
        throw violation("handoff.same_target");
    if (holder.available)
        return null;
    if (override === null)
        throw violation("handoff.target_unavailable");
    return override;
}
/**
 * H2: a WAITING handoff goes to another staff member and stays WAITING. The actor gate comes first (R24), then
 * the status, then who may move it, then the version, the reason and the new target.
 */
export function planHandoffReassign(input) {
    const { handoff } = input;
    const actor = assertStaffReachesHandoff(input.actor, "handoff.accept", input.environment);
    if (handoff.status !== "WAITING")
        throw violation("handoff.not_waiting");
    assertMayMove(actor, handoff.status);
    checkVersion(input.expectedVersion, handoff);
    const reason = requireReason(input.staffReason);
    const override = checkNewHolder(actor, input.newTarget, handoff.targetStaffUserId, input.availabilityOverride ?? null);
    return transitionPlan("reassign", handoff, "WAITING", {
        availabilityOverride: override,
        patch: {
            kind: "reassign",
            targetKind: "DIRECT",
            targetStaffUserId: input.newTarget.staffUserId,
            targetAssignmentId: null,
        },
        historyEvent: eventPlan(actor, "REASSIGNED", "WAITING", {
            targetKind: "DIRECT",
            targetStaffUserId: input.newTarget.staffUserId,
            internalReason: reason,
        }),
        auditAction: "handoff.reassigned",
    });
}
/** The coverage an escalation with this reason moves to: a wait that ran out never makes an exception, the others may. */
export const escalationTargetFor = (reasonCode, facts) => (reasonCode === "NOT_ACCEPTED_IN_TIME" ? facts.nextTimed : facts.next);
/**
 * The reason an automatic escalation has right now, or `null` when the handoff should stay where it is: the coverage is lost
 * (`PRIMARY_UNAVAILABLE`), else a CONFIGURED "remains unaccepted" threshold has passed (`NOT_ACCEPTED_IN_TIME`). The sweep uses
 * it to choose the code; the command judges the same facts again under its locks.
 */
export function dueEscalationReason(facts) {
    if (facts.coverageLost)
        return "PRIMARY_UNAVAILABLE";
    if (facts.escalateAfterMs !== null && facts.waitedMs >= facts.escalateAfterMs) {
        return "NOT_ACCEPTED_IN_TIME";
    }
    return null;
}
function assertEscalationDue(reasonCode, facts) {
    if (reasonCode === "PRIMARY_UNAVAILABLE" && !facts.coverageLost) {
        throw violation("handoff.escalation_not_needed");
    }
    if (reasonCode === "NOT_ACCEPTED_IN_TIME" &&
        (facts.escalateAfterMs === null || facts.waitedMs < facts.escalateAfterMs)) {
        throw violation("handoff.escalation_not_needed");
    }
}
/**
 * H2 as an escalation (contract section 14, item 13: a reassign with a reason code and a system or staff actor, no
 * new state): a WAITING handoff goes to the NEXT coverage and stays WAITING. Who: a supervisor, the current target,
 * or the system actor `handoff-escalation`. The order of the checks: the actor gate (R24), the status, who may move it,
 * the version, the reason code (`SUPERVISOR_OVERRIDE` is for a supervisor only), whether the reason holds NOW
 * (`handoff.escalation_not_needed`), and last whether there is a next coverage (`handoff.no_escalation_target`).
 * The count of escalations is bounded. The primary assignment is only read, never changed.
 */
export function planHandoffEscalation(input) {
    const { handoff } = input;
    let actor;
    if (input.actor.kind === "system") {
        if (input.actor.actorId !== "handoff-escalation")
            throw denied("role_missing");
        actor = input.actor;
    }
    else {
        actor = assertStaffReachesHandoff(input.actor, "handoff.accept", input.environment);
    }
    if (handoff.status !== "WAITING")
        throw violation("handoff.not_waiting");
    if (actor.kind === "staff")
        assertMayMove(actor, handoff.status);
    checkVersion(input.expectedVersion, handoff);
    if (input.reasonCode === "SUPERVISOR_OVERRIDE") {
        if (actor.kind !== "staff" || !isSupervisor(actor.authority))
            throw denied("role_missing");
    }
    assertEscalationDue(input.reasonCode, input.facts);
    const next = escalationTargetFor(input.reasonCode, input.facts);
    if (next === null || input.escalationCount >= MAX_HANDOFF_ESCALATIONS) {
        throw violation("handoff.no_escalation_target");
    }
    const targetStaffUserId = "staffUserId" in next ? next.staffUserId : null;
    const targetAssignmentId = "assignmentId" in next ? next.assignmentId : null;
    return transitionPlan("reassign", handoff, "WAITING", {
        patch: {
            kind: "escalate",
            targetKind: next.kind,
            targetStaffUserId,
            targetAssignmentId,
            escalatedAt: "now",
        },
        historyEvent: eventPlan(actor, "REASSIGNED", "WAITING", {
            targetKind: next.kind,
            targetStaffUserId,
            internalReason: `escalation:${input.reasonCode}`,
        }),
        auditAction: "handoff.escalated",
        outcome: {
            targetKind: next.kind,
            targetStaffUserId,
            fallback: next.fallback,
            degraded: next.degraded,
            skipped: skippedEvidence(next),
        },
    });
}
/** H4: an ACCEPTED handoff goes to another owner and stays ACCEPTED and HUMAN_ACTIVE. The same rules as H2. */
export function planOwnerTransfer(input) {
    const { handoff } = input;
    const actor = assertStaffReachesHandoff(input.actor, "handoff.accept", input.environment);
    if (handoff.status !== "ACCEPTED")
        throw violation("handoff.not_accepted");
    assertMayMove(actor, handoff.status);
    checkVersion(input.expectedVersion, handoff);
    const reason = requireReason(input.staffReason);
    const override = checkNewHolder(actor, input.newOwner, handoff.acceptedByStaffUserId, input.availabilityOverride ?? null);
    return transitionPlan("transfer_owner", handoff, "ACCEPTED", {
        availabilityOverride: override,
        patch: { kind: "transfer_owner", acceptedByStaffUserId: input.newOwner.staffUserId },
        historyEvent: eventPlan(actor, "OWNER_TRANSFERRED", "ACCEPTED", {
            targetKind: handoff.targetKind,
            targetStaffUserId: handoff.targetStaffUserId,
            ownerStaffUserId: input.newOwner.staffUserId,
            internalReason: reason,
        }),
        auditAction: "handoff.owner_transferred",
    });
}
// --- H5 release / H6-H7 cancel -----------------------------------------------------------------
export function planHandoffRelease(input) {
    const { handoff } = input;
    // The actor comes before the status (R24): a staff user with no relationship to the handoff gets
    // the same refusal in every status, so the status class is not revealed.
    const actor = assertStaffCapability(input.actor, "handoff.release", input.environment);
    let basis;
    if (actor.relationship.isOwner)
        basis = "OWNER";
    else if (isSupervisor(actor.authority))
        basis = "SUPERVISOR";
    else
        throw denied("relationship_missing");
    if (handoff.status !== "ACCEPTED")
        throw violation("handoff.not_accepted");
    checkVersion(input.expectedVersion, handoff);
    const reason = requireReason(input.staffReason);
    return transitionPlan("release", handoff, "RELEASED", {
        basis,
        patch: {
            kind: "release",
            resolvedAt: "now",
            resolvedByStaffUserId: actor.authority.staffUserId,
            resolutionReason: reason,
        },
        historyEvent: eventPlan(actor, "RELEASED", "RELEASED", {
            targetKind: handoff.targetKind,
            targetStaffUserId: handoff.targetStaffUserId,
            ownerStaffUserId: handoff.acceptedByStaffUserId,
            internalReason: reason,
        }),
        auditAction: "handoff.released",
    });
}
/** Supervisor-only cancel of a handoff nobody accepted yet; the conversation goes back to AI_ACTIVE. */
export function planHandoffCancel(input) {
    const { handoff } = input;
    if (handoff.status !== "REQUESTED" && handoff.status !== "WAITING") {
        throw violation("handoff.transition_invalid");
    }
    const actor = assertStaffCapability(input.actor, "handoff.cancel", input.environment);
    checkVersion(input.expectedVersion, handoff);
    const reason = requireReason(input.staffReason);
    return transitionPlan("cancel", handoff, "CANCELLED", {
        patch: {
            kind: "cancel",
            resolvedAt: "now",
            resolvedByStaffUserId: actor.authority.staffUserId,
            resolutionReason: reason,
        },
        historyEvent: eventPlan(actor, "CANCELLED", "CANCELLED", {
            targetKind: handoff.targetKind,
            targetStaffUserId: handoff.targetStaffUserId,
            internalReason: reason,
        }),
        auditAction: "handoff.cancelled",
    });
}
// --- customer-safe system notices --------------------------------------------------------------
/**
 * The fixed codes of the notice the customer's thread gets after a committed handoff transition
 * (contract section 7). A code carries no routing detail: the customer never learns who, which
 * coverage or why. The clients own the words. The list repeats `systemNoticeCodes` of
 * `@lafayette/schemas` (parity-tested there).
 */
export const handoffNoticeCodes = Object.freeze([
    "CONNECTING_TO_CONCIERGE",
    "CONCIERGE_JOINED",
    "CONCIERGE_CHANGED",
    "AI_RESUMED",
]);
/**
 * The notice a committed transition posts, or `null` when the customer sees nothing: a routing step
 * and a re-target of a WAITING handoff are internal (T5 posts nothing), and the request notice
 * covers T1 to T4 because a request routes in the same transaction.
 */
export function handoffNoticeCodeFor(action) {
    switch (action) {
        case "handoff.requested":
        case "handoff.requested_by_staff":
            return "CONNECTING_TO_CONCIERGE";
        case "handoff.accepted":
            return "CONCIERGE_JOINED";
        case "handoff.owner_transferred":
            return "CONCIERGE_CHANGED";
        case "handoff.released":
        case "handoff.cancelled":
            return "AI_RESUMED";
        case "handoff.routed":
        case "handoff.reassigned":
        case "handoff.escalated":
            return null;
    }
}
/**
 * The id of a system notice: deterministic per domain event (the handoff, the version the event
 * produced and the notice code), so a retry, a replay of the command or a second worker can never
 * post a second notice. The result has the shape of a uuid, the `noticeId` of the notice envelope,
 * and is NOT a secret and NOT a handoff id.
 */
export function handoffNoticeId(input) {
    const hex = sha256HexOfText(`lafayette:system-notice:${input.handoffId}:${input.handoffVersion}:${input.code}`);
    // Version 4 and variant bits, so every uuid parser accepts it.
    const variant = ((Number.parseInt(hex.charAt(16), 16) & 0x3) | 0x8).toString(16);
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        `4${hex.slice(13, 16)}`,
        `${variant}${hex.slice(17, 20)}`,
        hex.slice(20, 32),
    ].join("-");
}
/**
 * Whether the AI may answer ONE customer message (contract section 7, last paragraph; CHT-731). The rule:
 *
 * - The conversation is AI_ACTIVE and the reply's generation is the current one (the fence).
 * - The message was sent at or after `modeChangedAt`, the moment the conversation last changed mode.
 *
 * So a message that arrived while a person held the conversation (WAITING or HUMAN_ACTIVE) is stored and visible
 * to staff, creates no reply, and is NEVER answered by the AI after a release, even when its webhook is delivered
 * late. After the release the AI answers only messages sent at or after the release. A message sent a moment before
 * the release commits belongs to the human period. `modeChangedAt` and the message time come from the database
 * (the conversation row and the message reference), never from a client clock.
 */
export function decideAiAnswerToCustomerMessage(input) {
    if (input.mode !== "AI_ACTIVE")
        return { allowed: false, code: "mode_not_ai_active" };
    if (input.replyGeneration !== input.aiResponseGeneration) {
        return { allowed: false, code: "generation_stale" };
    }
    if (input.messageSentAt.getTime() < input.modeChangedAt.getTime()) {
        return { allowed: false, code: "message_before_resume" };
    }
    return { allowed: true };
}
